import { Injectable, Scopes, ControllerProtocol } from '@visiba/instantiation';
import { AnalyticCategories, AnalyticService } from '@application/analytic/analytic.service';
import { BookingScreen, ViewBookingAnalyticActions, ViewBookingController } from '../view_booking.controller';
import { BookingBasketService } from '../booking_basket.service';
import { signal } from '@preact/signals-react';
import { TimeSlotApiService } from '@api_patient/timeslot_api.service';
import { LocalityType } from '@api_patient/generated/clientv2/models/locality-type';
import type {
	ClientPhysicalLocationApiModel,
	ClientResourceModel,
	ClientTimeslotApiModel,
	CurrencyDisplay,
	SearchCapabilitiesApiModel,
} from '@api_patient/generated/clientv2/models';
import type { AsyncSignalValue } from '@component/async.component';
import { UniversalConfig } from '@application/universal_config.service';
import { DateService } from '@application/time/date.service';
import { Dayjs } from 'dayjs';
import { safeNumberParse } from '@visiba/std';

interface SelectedFilters {
	resourceId: number | null;
	specialityId: number | null;
	unitId: number | null;
}

interface Filters {
	resources: SearchCapabilitiesApiModel['Resources'];
	specialities: SearchCapabilitiesApiModel['Specialities'];
	units: SearchCapabilitiesApiModel['FilterableUnits'];
}

interface DateSpan {
	from: Nullable<Dayjs>;
	to: Nullable<Dayjs>;
}

@Injectable({
	scope: Scopes.Scoped,
})
export class ScreenTimeSlotController implements ControllerProtocol {
	public location = signal<Nullable<ClientPhysicalLocationApiModel>>(null);
	public currentDate = signal<Nullable<Dayjs>>(null);
	public dateSpan = signal<DateSpan>({ from: null, to: null });
	public availability = signal<{ dates: Map<string, number>; next: Nullable<Dayjs> }>({ dates: new Map(), next: null });
	public nextAvailableDate = signal<Nullable<Dayjs>>(null);
	public timeSlots = signal<AsyncSignalValue<ClientTimeslotApiModel[]>>({
		value: null,
		flowState: 'pending',
	});
	public timeSlotDetails = signal<AsyncSignalValue<TimeSlotDetails>>({
		value: null,
		flowState: 'idle',
	});
	public filters = signal<AsyncSignalValue<Filters>>({
		value: null,
		flowState: 'idle',
	});
	public selectedFilters = signal<SelectedFilters>({ resourceId: null, specialityId: null, unitId: null });

	public showUnavailableTimeSlotDialog = signal<boolean>(false);
	public localityType: LocalityType = LocalityType.Digital;

	constructor(
		private readonly viewBookingController: ViewBookingController,
		private readonly analyticService: AnalyticService,
		private readonly bookingBasketService: BookingBasketService,
		private readonly timeSlotApiService: TimeSlotApiService,
		private readonly universalConfig: UniversalConfig,
		private readonly dateService: DateService,
	) {}

	async whenMount() {
		this.analyticService.trackPageView({
			customTitle: 'Booking: Time slot',
		});

		if (!this.allowedToBeHere()) {
			this.analyticService.trackEvent(AnalyticCategories.Booking, ViewBookingAnalyticActions.NotAllowedToBeHere, 'Time slot');
			this.viewBookingController.goToScreen(BookingScreen.Location, { replace: true });

			return;
		}

		const { timeSlotFilterResourceId, timeSlotFilterSpecialityId, timeSlotFilterUnitId } = this.bookingBasketService.get();
		const flowSettings = this.viewBookingController.getFlowSettings();
		this.selectedFilters.value = {
			resourceId: flowSettings.filterResourceId ?? timeSlotFilterResourceId,
			specialityId: flowSettings.filterSpecialityId ?? timeSlotFilterSpecialityId,
			unitId: flowSettings.filterUnitId ?? timeSlotFilterUnitId,
		};
	}

	async setup(localityType: LocalityType, locationId?: string) {
		this.populateDateSpan();

		// Setting location
		this.localityType = localityType;
		this.location.value =
			this.viewBookingController.flowData.peek().value?.physicalLocations.find((location) => {
				return location.Id.toString() == locationId;
			}) ?? null;

		// Fetch, populate and set filters
		this.fetchFilters();

		// Set current date and check availability
		const basketData = this.bookingBasketService.get();
		const date = basketData.currentDate ?? undefined;

		this.currentDate.value = date ?? this.dateService.newDate();

		const availabilityResult = await this.getAvailability();
		if (availabilityResult.err) {
			this.analyticService.trackEvent(AnalyticCategories.Booking, ViewBookingAnalyticActions.ErrorFetchingAvailability);
			this.viewBookingController.goToScreen(BookingScreen.Error);

			return;
		}

		const availability = this.availability.peek();

		let shouldFetchTimeSlots = false;

		if (date != null) {
			// If date is set and there is available time slots that day, we should fetch time slots.
			if (availability.dates.has(date.format('YYYY-MM-DD'))) {
				shouldFetchTimeSlots = true;
			}

			// If no date is set, try to figure out next available date to set.
		} else {
			// If there is no available time slot current week and there is time slots
			// in the future. Set current date to that date.
			if (availability.dates.size === 0 && availability.next != null) {
				await this.setCurrentDate(availability.next);

				return;
			}

			// If there is available time slots this week, set current date to the nearest.
			if (availability.dates.size > 0) {
				const firstAvailableDate = availability.dates.entries().next().value?.[0] ?? undefined;
				this.currentDate.value = this.dateService.newDate(firstAvailableDate, 'YYYY-MM-DD');

				shouldFetchTimeSlots = true;
			}
		}

		if (shouldFetchTimeSlots) {
			const timeSlotResult = await this.loadTimeSlots();
			if (timeSlotResult.err) {
				this.analyticService.trackEvent(AnalyticCategories.Booking, ViewBookingAnalyticActions.ErrorFetchingTimeSlots);
				this.viewBookingController.goToScreen(BookingScreen.Error);
			}
		} else {
			this.timeSlots.value = {
				value: [],
				flowState: 'accessible',
			};
		}

		this.setNextAvailableDate();
	}

	public async fetchFilters() {
		this.filters.value = {
			value: null,
			flowState: 'pending',
		};

		const capabilitiesResult = await this.timeSlotApiService.getSearchCapabilities(this.universalConfig.selectedUnitId);
		if (capabilitiesResult.err) {
			this.filters.value = {
				value: null,
				flowState: 'error',
			};

			return;
		}

		const flowSettings = this.viewBookingController.getFlowSettings();

		this.filters.value = {
			value: {
				resources: flowSettings.lockFilters
					? (capabilitiesResult.ok.data.Resources?.filter((resource) => resource.Id === flowSettings.filterResourceId) ?? null)
					: capabilitiesResult.ok.data.Resources,
				specialities: flowSettings.lockFilters
					? (capabilitiesResult.ok.data.Specialities?.filter((speciality) => speciality.Id === flowSettings.filterSpecialityId) ??
						null)
					: capabilitiesResult.ok.data.Specialities,
				units: flowSettings.lockFilters
					? (capabilitiesResult.ok.data.FilterableUnits?.filter((unit) => unit.Id === flowSettings.filterUnitId) ?? null)
					: capabilitiesResult.ok.data.FilterableUnits,
			},
			flowState: 'accessible',
		};
	}

	public setFilter(filters: SelectedFilters) {
		this.selectedFilters.value = filters;
		this.bookingBasketService.set({
			timeSlotFilterResourceId: filters.resourceId,
			timeSlotFilterSpecialityId: filters.specialityId,
			timeSlotFilterUnitId: filters.unitId,
		});

		this.setCurrentDate(this.currentDate.peek() ?? this.dateService.newDate(), true);
	}

	public async setCurrentDate(date: Dayjs, forceUpdate = false) {
		const currentDate = this.currentDate.peek();

		if (currentDate && date.isSame(currentDate, 'day') && !forceUpdate) {
			return;
		}

		const isNewWeek = currentDate == null || !currentDate.isSame(date, 'week') || forceUpdate;

		this.currentDate.value = date;

		if (isNewWeek) {
			const availabilityResult = await this.getAvailability();
			if (availabilityResult.err) {
				this.analyticService.trackEvent(AnalyticCategories.Booking, ViewBookingAnalyticActions.ErrorFetchingAvailability);
				this.viewBookingController.goToScreen(BookingScreen.Error);

				return;
			}
		}

		if (this.availability.peek().dates.has(date.format('YYYY-MM-DD'))) {
			const timeslotResult = await this.loadTimeSlots();
			if (timeslotResult.err) {
				this.analyticService.trackEvent(AnalyticCategories.Booking, ViewBookingAnalyticActions.ErrorFetchingTimeSlots);
				this.viewBookingController.goToScreen(BookingScreen.Error);

				return;
			}
		} else {
			this.timeSlots.value = {
				value: [],
				flowState: 'accessible',
			};
		}

		this.bookingBasketService.set({
			currentDate: date.format('YYYY-MM-DD'),
		});

		this.setNextAvailableDate();
	}

	public async getAvailability() {
		const flowSettings = this.viewBookingController.getFlowSettings();
		const basket = this.bookingBasketService.get();

		const currentDate = this.currentDate.peek();
		if (!currentDate) {
			return Err('No current date');
		}

		const dateSpan = this.dateSpan.peek();
		let startDate = currentDate.startOf('week');
		let endDate = startDate.endOf('week');

		if (dateSpan.from && startDate.isBefore(dateSpan.from, 'day')) {
			startDate = dateSpan.from;
		}

		if (dateSpan.to && endDate.isAfter(dateSpan.to, 'day')) {
			endDate = dateSpan.to;
		}

		const selectedFilters = this.selectedFilters.peek();

		const availabilityCountResult = await this.timeSlotApiService.availabilityCounts(this.universalConfig.selectedUnitId, {
			searchContextId: flowSettings.searchContextId ?? undefined,
			conditionId: basket.reasonForVisit ?? undefined,
			fromDate: startDate.format('YYYY-MM-DD'),
			toDate: endDate.format('YYYY-MM-DD'),
			fromTime: '00:00:00',
			toTime: '23:59:59',
			localityType: this.localityType,
			physicalLocationId: this.location.peek()?.Id,
			resourceId: selectedFilters.resourceId ?? undefined,
			specialityId: selectedFilters.specialityId ?? undefined,
			filterableUnitId: selectedFilters.unitId ?? undefined,
		});

		if (availabilityCountResult.err) {
			return Err(availabilityCountResult.err);
		}

		const yesterday = this.dateService.newDate().subtract(1, 'day');

		const dates = new Map<string, number>();
		availabilityCountResult.ok.data.AvailableDates.filter((availability) => yesterday.isBefore(availability.Date, 'day')).map(
			(availability) => {
				dates.set(availability.Date, availability.Count);
			},
		);

		let nextAvailableDate = availabilityCountResult.ok.data.NextAvailableDate
			? this.dateService.newDate(availabilityCountResult.ok.data.NextAvailableDate, 'YYYY-MM-DD')
			: null;
		if (
			nextAvailableDate &&
			((dateSpan.to && nextAvailableDate.isAfter(dateSpan.to, 'day')) || nextAvailableDate.isSame(currentDate, 'day'))
		) {
			nextAvailableDate = null;
		}

		this.availability.value = {
			dates: dates,
			next: nextAvailableDate,
		};

		return Ok();
	}

	public setNextAvailableDate() {
		const currentDate = this.currentDate.peek();
		if (!currentDate) {
			return Err('No current date');
		}

		let nextAvailableDate = null;

		for (const [date, count] of this.availability.peek().dates) {
			if (this.dateService.newDate(date, 'YYYY-MM-DD').isAfter(currentDate, 'day') && count > 0) {
				nextAvailableDate = this.dateService.newDate(date);

				break;
			}
		}

		// If there are no available dates in the week, check if there is an available date in the future.
		if (!nextAvailableDate) {
			const nextUpcomingDate = this.availability.peek().next;
			nextAvailableDate = nextUpcomingDate?.isAfter(currentDate, 'day') ? nextUpcomingDate : null;
		}

		this.nextAvailableDate.value = nextAvailableDate;

		return Ok();
	}

	public async loadTimeSlots() {
		const flowSettings = this.viewBookingController.getFlowSettings();
		const basket = this.bookingBasketService.get();

		const currentDate = this.currentDate.peek();
		if (!currentDate) {
			return Err('No current date');
		}

		this.timeSlots.value = {
			value: null,
			flowState: 'pending',
		};

		const selectedFilters = this.selectedFilters.peek();

		const timeSlotsResult = await this.timeSlotApiService.getTimeSlots(this.universalConfig.selectedUnitId, {
			searchContextId: flowSettings.searchContextId ?? undefined,
			conditionId: basket.reasonForVisit ?? undefined,
			fromDate: currentDate.format('YYYY-MM-DD'),
			toDate: currentDate.format('YYYY-MM-DD'),
			fromTime: '00:00:00',
			toTime: '23:59:59',
			localityType: this.localityType,
			physicalLocationId: this.location.peek()?.Id,
			resourceId: selectedFilters.resourceId ?? undefined,
			specialityId: selectedFilters.specialityId ?? undefined,
			filterableUnitId: selectedFilters.unitId ?? undefined,
		});

		if (timeSlotsResult.err) {
			this.timeSlots.value = {
				value: null,
				flowState: 'error',
			};

			return Err(timeSlotsResult.err);
		}

		this.timeSlots.value = {
			value: timeSlotsResult.ok.data,
			flowState: 'accessible',
		};

		return Ok();
	}

	public async previewTimeSlot(timeSlotId: string) {
		const timeSlotResult = await this.getTimeSlot(timeSlotId);

		if (timeSlotResult.err) {
			this.analyticService.trackEvent(AnalyticCategories.Booking, ViewBookingAnalyticActions.ErrorFetchingTimeSlotDetails);

			return Err(timeSlotResult.err);
		}

		const selectedTimeSlot = timeSlotResult.ok;

		const lockResult = await this.bookingBasketService.lockTimeSlot(timeSlotId);
		if (lockResult.err) {
			this.loadTimeSlots();

			this.analyticService.trackEvent(AnalyticCategories.Booking, ViewBookingAnalyticActions.ErrorWhileLockingTimeSlot);

			this.showUnavailableTimeSlotDialog.value = true;
			this.clearSelectTimeSlot();

			return Err(lockResult.err);
		}

		let physicalLocation: ClientPhysicalLocationApiModel | null = null;

		if (selectedTimeSlot.PhysicalLocationId) {
			physicalLocation =
				this.viewBookingController.flowData
					.peek()
					.value?.physicalLocations.find((location) => location.Id === selectedTimeSlot.PhysicalLocationId) ?? null;
		}

		this.timeSlotDetails.value = {
			flowState: 'accessible',
			value: {
				physicalLocation,
				requiresPayment: selectedTimeSlot.RequiresPayment,
				duration: selectedTimeSlot.Duration,
				id: selectedTimeSlot.Id ?? 'NO_ID',
				description: selectedTimeSlot.Name,
				price: selectedTimeSlot.Price,
				resource: selectedTimeSlot.Resource,
				start: selectedTimeSlot.Start,
				unbookableAfter: selectedTimeSlot.UnbookableAfter,
			},
		};

		return Ok();
	}

	public clearTimeSlotDetails() {
		this.timeSlotDetails.value = {
			flowState: 'idle',
			value: null,
		};

		this.clearSelectTimeSlot();

		return this.bookingBasketService.unlockTimeSlot();
	}

	public clearSelectTimeSlot() {
		this.bookingBasketService.set({
			timeSlotId: null,
		});
	}

	public async selectTimeSlot(timeSlotId: string, redirect = true) {
		this.bookingBasketService.set({
			timeSlotId: timeSlotId,
		});

		if (redirect) {
			this.viewBookingController.goToScreen(BookingScreen.Forms);
		}
	}

	private async getTimeSlot(timeSlotId: string): AsyncResult<ClientTimeslotApiModel> {
		const selectedTimeslot = this.timeSlots.peek().value?.find((timeSlot) => timeSlot.Id === timeSlotId);

		if (selectedTimeslot) return Ok(selectedTimeslot);

		const timeSlotResult = await this.timeSlotApiService.getTimeSlot(this.universalConfig.selectedUnitId, timeSlotId);

		if (timeSlotResult.err) {
			return Err(timeSlotResult.err);
		}

		return Ok(timeSlotResult.ok.data);
	}

	private allowedToBeHere() {
		// If we don't have flow data, we can't be here.
		const flowData = this.viewBookingController.flowData.peek().value;
		if (!flowData) {
			return false;
		}

		// If we don't have a selected respondent type, we can't be here.
		if (this.bookingBasketService.get().respondantType == null) {
			return false;
		}

		// If we have reason for visits, we need to have a selected reason for visit.
		if (
			flowData.useReasonForVisits &&
			this.viewBookingController.getFilteredReasonForVisits().length > 0 &&
			!this.viewBookingController.getSelectedReasonForVisit()
		) {
			return false;
		}

		return true;
	}
	private populateDateSpan() {
		const flowSettings = this.viewBookingController.getFlowSettings();
		let dateSpan: DateSpan = {
			from: null,
			to: null,
		};

		// Get date span from search context, if there is a search context, and
		// create DayJS instances
		if (flowSettings.searchContext) {
			const searchContext = flowSettings.searchContext;
			if (searchContext.FirstSearchDate != null) {
				dateSpan.from = this.dateService.newDate(searchContext.FirstSearchDate);
			}

			if (searchContext.LastSearchDate != null) {
				dateSpan.to = this.dateService.newDate(searchContext.LastSearchDate);
			}

			if (searchContext.RelativeStart != null) {
				const fromDate = this.addDurationToDate(searchContext.RelativeStart);

				if (dateSpan.from == null || dateSpan.from.isBefore(fromDate, 'day')) {
					dateSpan.from = fromDate;
				}
			}

			if (searchContext.RelativeEnd != null) {
				const toDate = this.addDurationToDate(searchContext.RelativeEnd);

				if (dateSpan.to == null || dateSpan.to.isAfter(toDate, 'day')) {
					dateSpan.to = toDate;
				}
			}
		}

		// It is possible to include fromDate and toDate as parameters in the
		// deeplink. If there is a search context that defines a broader date range (initiated above),
		// and the fromDate and toDate specified in the deeplink fall within that
		// range, the original date span will be overridden with the narrower range
		// provided by the deeplink.

		// If there is no search context, we simply use the fromDate and toDate
		// specified in the deeplink.
		if (flowSettings.fromDate && (dateSpan.from == null || dateSpan.from.isBefore(flowSettings.fromDate, 'day'))) {
			dateSpan.from = flowSettings.fromDate;
		}
		if (flowSettings.toDate && (dateSpan.to == null || dateSpan.to.isAfter(flowSettings.toDate, 'day'))) {
			dateSpan.to = flowSettings.toDate;
		}

		this.dateSpan.value = dateSpan;
	}
	private addDurationToDate(duration: Duration | string) {
		let date = this.dateService.newDate();
		function parseDurationFormat(value: string): Duration {
			const splittedValue = value.split(':').reverse();

			return {
				seconds: safeNumberParse(splittedValue[0]) ?? 0,
				minutes: safeNumberParse(splittedValue[1]) ?? 0,
				hours: safeNumberParse(splittedValue[2]) ?? 0,
			};
		}

		if (typeof duration === 'string') {
			duration = parseDurationFormat(duration);
		}

		date = date.add(duration.hours, 'hours').add(duration.minutes, 'minutes').add(duration.seconds, 'seconds');

		return date;
	}
}

type Duration = {
	hours: number;
	minutes: number;
	seconds: number;
};

export interface TimeSlotDetails {
	duration: number;
	id: string;
	description: string | null;
	price: CurrencyDisplay | null;
	requiresPayment: boolean;
	resource: ClientResourceModel | null;
	start: string;
	unbookableAfter: string;
	physicalLocation: ClientPhysicalLocationApiModel | null;
}
